/**
 * BookshelfContext - Global state for Virtual Bookshelf
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/lib/useAuth';
import { DEFAULT_SHELVES } from '../constants';
import { SEED_BOOKS } from '../constants/seedBooks';

const BookshelfContext = createContext(undefined);

// The API names system shelves freely ("All", "Currently Reading", ...) and gives
// them its own ids. Map them back to the canonical ids the filters and icons use.
const SYSTEM_SHELF_BY_NAME = {
  all: 'shelf_all',
  'all books': 'shelf_all',
  tbr: 'shelf_tbr',
  'to be read': 'shelf_tbr',
  reading: 'shelf_reading',
  'currently reading': 'shelf_reading',
  finished: 'shelf_finished',
  read: 'shelf_finished',
  favorites: 'shelf_favorites',
  favourites: 'shelf_favorites',
};

const canonicalSystemId = (shelf) => {
  if (shelf.type !== 'system') return null;
  if (String(shelf.id).startsWith('shelf_')) return shelf.id;
  const byName = SYSTEM_SHELF_BY_NAME[String(shelf.name ?? '').trim().toLowerCase()];
  return byName ?? null;
};

/**
 * Provider component that wraps the bookshelf feature
 */
export function BookshelfProvider({ children }) {
  const { user, getShelves, getBooks, addBookToShelf, createShelf: createShelfApi } = useAuth();
  // Core data
  const [shelves, setShelves] = useState(DEFAULT_SHELVES);
  const [books, setBooks] = useState(SEED_BOOKS);
  const [activeShelf, setActiveShelf] = useState('shelf_all');
  
  // Filters & Sort
  const [filters, setFilters] = useState({
    genres: [],
    status: [],
    rating: null,
    isFavoriteOnly: false,
    searchQuery: '',
  });
  const [sortBy, setSortBy] = useState('dateAdded');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return undefined;

    let active = true;

    getShelves()
      .then((payload) => {
        if (!active) return;
        const remoteShelves = payload?.data?.shelves || payload?.data || payload?.shelves || payload;
        if (!Array.isArray(remoteShelves)) return;

        const shelvesFromApi = remoteShelves.map((shelf) => ({
          ...shelf,
          id: shelf.id ?? shelf.shelfId,
          name: shelf.name ?? shelf.title ?? 'Untitled shelf',
          type: shelf.type ?? (shelf.isSystem ? 'system' : 'custom'),
          bookCount: shelf.bookCount ?? shelf.book_count ?? shelf.count ?? 0,
          createdAt: shelf.createdAt ?? shelf.created_at ?? Date.now(),
          updatedAt: shelf.updatedAt ?? shelf.updated_at ?? Date.now(),
        })).filter((shelf) => shelf.id);

        const normalized = shelvesFromApi.map((shelf) => {
          const canonicalId = canonicalSystemId(shelf);
          return canonicalId ? { ...shelf, id: canonicalId } : shelf;
        });

        if (normalized.length > 0) setShelves(normalized);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load bookshelves');
      });

    return () => { active = false; };
  }, [getShelves, user]);

  useEffect(() => {
    if (!user) return undefined;

    let active = true;
    setLoading(true);

    getBooks()
      .then((payload) => {
        if (!active) return;
        const remoteBooks = payload?.data?.books || payload?.data?.items || payload?.data?.results || payload?.data || payload?.books || payload?.items || payload?.results || payload;
        if (!Array.isArray(remoteBooks)) return;

        const normalizeAuthor = (author) => {
          if (typeof author === 'string') return author;
          return author?.name || author?.displayName || '';
        };

        const booksFromApi = remoteBooks.map((book) => ({
          ...book,
          id: book.id ?? book.bookId,
          title: book.title ?? 'Untitled',
          author: normalizeAuthor(book.author) || normalizeAuthor(book.authors?.[0]) || 'Unknown Author',
          coverUrl: book.coverUrl ?? book.cover_url ?? undefined,
          genres: book.genres ?? book.subjects ?? [],
          status: String(book.status ?? 'tbr').toLowerCase().replace(/^currently[_ -]?reading$/, 'reading').replace(/^to[_ -]?be[_ -]?read$/, 'tbr'),
          isFavorite: book.isFavorite ?? false,
          customShelfIds: book.customShelfIds ?? book.custom_shelf_ids ?? book.shelfIds ?? book.shelf_ids ?? [],
          dateAdded: book.dateAdded ?? book.date_added ?? book.createdAt ?? book.created_at ?? Date.now(),
        })).filter((book) => book.id);

        setBooks(booksFromApi);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load books');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [getBooks, user]);

  /**
   * Calculate reading statistics
   */
  const stats = {
    tbr: books.filter(b => b.status === 'tbr').length,
    currentlyReading: books.filter(b => b.status === 'reading').length,
    finished: books.filter(b => b.status === 'finished').length,
    favorites: books.filter(b => b.isFavorite).length,
  };

  /**
   * Filter and sort books based on current filters and sort
   */
  const filteredBooks = (() => {
    let result = [...books];

    // Filter by active shelf (except "All Books")
    if (activeShelf !== 'shelf_all') {
      const shelfConfig = {
        shelf_tbr: (b) => b.status === 'tbr',
        shelf_reading: (b) => b.status === 'reading',
        shelf_finished: (b) => b.status === 'finished',
        shelf_favorites: (b) => b.isFavorite,
      };

      const filterFn = shelfConfig[activeShelf]
        || ((b) => (b.customShelfIds ?? []).includes(activeShelf));
      result = result.filter(filterFn);
    }

    // Apply filters
    if (filters.genres.length > 0) {
      result = result.filter(b =>
        b.genres.some(g => filters.genres.includes(g))
      );
    }

    if (filters.status.length > 0) {
      result = result.filter(b => filters.status.includes(b.status));
    }

    if (filters.rating !== null) {
      result = result.filter(b => (b.rating ?? 0) >= filters.rating);
    }

    if (filters.isFavoriteOnly) {
      result = result.filter(b => b.isFavorite);
    }

    if (filters.searchQuery?.trim()) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(b =>
        b.title.toLowerCase().includes(query) ||
        b.author.toLowerCase().includes(query)
      );
    }

    // Sort
    const sortFunctions = {
      dateAdded: (a, b) => b.dateAdded - a.dateAdded,
      title: (a, b) => a.title.localeCompare(b.title),
      author: (a, b) => a.author.localeCompare(b.author),
      rating: (a, b) => (b.rating ?? 0) - (a.rating ?? 0),
      progress: (a, b) => (b.percentageRead ?? 0) - (a.percentageRead ?? 0),
      dateStarted: (a, b) => (b.dateStarted ?? 0) - (a.dateStarted ?? 0),
    };

    const sortFn = sortFunctions[sortBy];
    if (sortFn) {
      result.sort(sortFn);
    }

    return result;
  })();

  /**
   * Add book to shelf
   */
  const addBook = useCallback(async (book, shelfId) => {
    try {
      setLoading(true);
      await addBookToShelf({ bookId: book.id, status: book.status, shelfId });
      setBooks(prev => [...prev, book]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add book');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addBookToShelf]);

  /**
   * Remove book from shelf
   */
  const removeBook = useCallback(async (bookId) => {
    try {
      setLoading(true);
      // TODO: API call
      // await API.removeBook(bookId);
      setBooks(prev => prev.filter(b => b.id !== bookId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove book');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update book (rating, progress, status, notes)
   */
  const updateBook = useCallback(
    async (bookId, updates) => {
      try {
        setLoading(true);
        // TODO: API call
        // await API.updateBook(bookId, updates);
        setBooks(prev =>
          prev.map(b => (b.id === bookId ? { ...b, ...updates } : b))
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update book');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Move book to different shelf
   */
  const moveBook = useCallback(async (bookId, toShelfId) => {
    try {
      setLoading(true);

      const shelfStatusMap = {
        shelf_tbr: 'tbr',
        shelf_reading: 'reading',
        shelf_finished: 'finished',
      };

      if (toShelfId === 'shelf_favorites') {
        await updateBook(bookId, { isFavorite: true });
      } else if (shelfStatusMap[toShelfId]) {
        await updateBook(bookId, { status: shelfStatusMap[toShelfId] });
      } else {
        // Custom shelf — add to its membership list rather than status
        setBooks(prev =>
          prev.map(b =>
            b.id === bookId
              ? { ...b, customShelfIds: [...new Set([...(b.customShelfIds ?? []), toShelfId])] }
              : b
          )
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move book');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [updateBook]);

  /**
   * Toggle a book's favorite state
   */
  const toggleFavorite = useCallback(async (bookId) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    await updateBook(bookId, { isFavorite: !book.isFavorite });
  }, [books, updateBook]);

  /**
   * Create custom shelf
   */
  const createShelf = useCallback(
    async (shelfData) => {
      try {
        setLoading(true);
        const payload = await createShelfApi(shelfData);
        const remoteShelf = payload?.data?.shelf || payload?.data || payload?.shelf || payload || {};
        const newShelf = {
          ...shelfData,
          ...remoteShelf,
          id: remoteShelf.id ?? remoteShelf.shelfId,
          name: remoteShelf.name ?? shelfData.name,
          type: remoteShelf.type ?? 'custom',
          bookCount: remoteShelf.bookCount ?? remoteShelf.book_count ?? 0,
          createdAt: remoteShelf.createdAt ?? remoteShelf.created_at ?? Date.now(),
          updatedAt: remoteShelf.updatedAt ?? remoteShelf.updated_at ?? Date.now(),
        };

        setShelves(prev => [...prev, newShelf]);
        return newShelf;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create shelf');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [createShelfApi]
  );

  /**
   * Update shelf
   */
  const updateShelf = useCallback(async (shelfId, updates) => {
    try {
      setLoading(true);
      // TODO: API call
      // await API.updateShelf(shelfId, updates);
      setShelves(prev =>
        prev.map(s =>
          s.id === shelfId
            ? { ...s, ...updates, updatedAt: Date.now() }
            : s
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update shelf');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete custom shelf
   */
  const deleteShelf = useCallback(async (shelfId) => {
    try {
      setLoading(true);
      // TODO: API call
      // await API.deleteShelf(shelfId);

      // Move books from deleted shelf to "All Books"
      setBooks(prev =>
        prev.map(b => ({
          ...b,
          // Update shelf associations if needed
        }))
      );

      setShelves(prev => prev.filter(s => s.id !== shelfId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete shelf');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => setError(null), []);

  /**
   * Shelves with live book counts (system shelves derive from status,
   * custom shelves from membership) instead of the static placeholder.
   */
  const shelvesWithCounts = shelves.map(shelf => {
    if (shelf.id === 'shelf_all') return { ...shelf, bookCount: books.length };
    if (shelf.id === 'shelf_tbr') return { ...shelf, bookCount: stats.tbr };
    if (shelf.id === 'shelf_reading') return { ...shelf, bookCount: stats.currentlyReading };
    if (shelf.id === 'shelf_finished') return { ...shelf, bookCount: stats.finished };
    if (shelf.id === 'shelf_favorites') return { ...shelf, bookCount: stats.favorites };
    return {
      ...shelf,
      bookCount: books.filter(b => (b.customShelfIds ?? []).includes(shelf.id)).length,
    };
  });

  const value = {
    shelves: shelvesWithCounts,
    books,
    activeShelf,
    stats,
    filters,
    sortBy,
    filteredBooks,
    addBook,
    removeBook,
    updateBook,
    moveBook,
    toggleFavorite,
    createShelf,
    updateShelf,
    deleteShelf,
    setActiveShelf,
    setFilters,
    setSortBy,
    loading,
    error,
    clearError,
  };

  return (
    <BookshelfContext.Provider value={value}>
      {children}
    </BookshelfContext.Provider>
  );
}

/**
 * Hook to use bookshelf context
 */
export function useBookshelf() {
  const context = useContext(BookshelfContext);
  if (!context) {
    throw new Error('useBookshelf must be used within BookshelfProvider');
  }
  return context;
}
